/**
 * Applying downloaded changes to Dexie.
 *
 * WHY THIS IS ITS OWN FILE
 * ------------------------
 * Because it is the only place in the product that writes business rows that
 * this device did not create, and it has to get six things right that no other
 * writer does:
 *
 *  1. ONE TRANSACTION for the whole batch. A batch that half-applies leaves the
 *     database in a state that never existed in any business's history, and
 *     because it looks consistent, nothing will ever notice.
 *  2. VERSION-AWARE. An incoming change at or below the local version is
 *     skipped — unless it is an EQUAL version carrying a different mutation, which
 *     is rule 5 below and is the one exception to "skipped".
 *  3. CONFLICT-AWARE. The decision comes from the domain model, not from here.
 *     This file asks "is this safe to apply?"; `conflictModel.ts` decides.
 *  4. IT NEVER DESTROYS A ROW OR A MOVEMENT. A deletion arrives as a TOMBSTONE
 *     ON THE ROW, never as a `delete()`. Removing the row is not "tidier": it
 *     throws away the version the tombstone was defending, and the second device
 *     is then holding something the first one is not.
 *  5. AN EQUAL VERSION IS NOT AUTOMATICALLY A RETRY. Two devices editing one row
 *     from the same base both compute version 2, so the number cannot tell "this
 *     change again" from "somebody else's change at the same number" — and
 *     assuming the first is how one device's edit was deleted in silence, with no
 *     conflict and no report. So an equal version is classified by mutation
 *     identity first: a retry is skipped, a collision is asked about and recorded.
 *  6. A ROW IT HAS NEVER SEEN IS NOT A DIVERGENCE. Conflict classification needs
 *     an existing local state to diverge from. With no local row the incoming
 *     entity is simply new here, whatever its status — a finalized invoice and a
 *     stock movement both belong on a device that does not hold them yet.
 */
import type { Transaction } from 'dexie';
import type { DNRDatabase } from '@data/db/database';
import type { SyncChange } from '@domain/sync/changeModel';
import { validateChange } from '@domain/sync/changeModel';
import {
  classOfEntity,
  resolveConflict,
  type SyncConflict,
} from '@domain/sync/conflictModel';
import {
  documentStatusOf,
  explainConflict,
  pendingChangeForStorage,
  statusOfRecord,
} from '@domain/sync/conflictRecovery';
import { allowedTransitions, type DocStatus } from '@domain/lifecycle';

const isDiagnosticEnabled = () =>
  !!(globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
    ?.DNR_SYNC_DIAGNOSTIC;

/**
 * Which table each entity type lands in.
 *
 * An explicit map, never a dynamic table name built from a wire string.
 * `validateChange` has already refused anything outside the syncable list, so
 * this lookup is total — and adding an entity type without a table is a
 * TypeScript error rather than a write to `undefined`.
 */
const TABLE_FOR = {
  business: 'businesses',
  party: 'parties',
  product: 'products',
  category: 'categories',
  taxRate: 'taxRates',
  stockLocation: 'stockLocations',
  document: 'documents',
  documentLine: 'documentLines',
  documentLink: 'documentLinks',
  payment: 'payments',
  paymentAllocation: 'paymentAllocations',
  expense: 'expenses',
  stockTransaction: 'stockTransactions',
} as const satisfies Record<string, string>;

const TABLE_NAME_FOR: ReadonlyMap<string, string> = new Map(Object.entries(TABLE_FOR));

/**
 * The Dexie table an entity type lands in, for code OUTSIDE this file.
 *
 * Exposed because resolving a conflict has to read the local row's current
 * version in the same transaction that closes the conflict, and it must do that
 * through the same map this file applies changes with — a second copy of the
 * mapping is a second place to forget an entity type.
 *
 * Returns undefined rather than guessing: an unknown type is a bug upstream, and
 * a resolver that guessed `parties` for it would read the wrong row.
 */
export function tableNameFor(entityType: string): string | undefined {
  return TABLE_NAME_FOR.get(entityType);
}

export interface ApplyResult {
  readonly applied: number;
  readonly skipped: number;
  readonly conflicts: SyncConflict[];
  /** Human-readable reasons. Surfaced in the status, not swallowed. */
  readonly rejected: string[];
}

export interface ApplyInput {
  readonly db: DNRDatabase;
  readonly businessId: string;
  readonly deviceId: string;
  readonly changes: readonly SyncChange[];
  readonly now: string;
  /**
   * True when these changes come from a full snapshot rather than a delta.
   *
   * A snapshot has no meaningful "newer than local" relationship — it is simply
   * the truth as of its cursor — so conflicts are not raised during recovery.
   * Raising them would block a device from ever recovering.
   */
  readonly fromSnapshot: boolean;
}

/**
 * Validate, then apply, in ONE transaction.
 *
 * A rejected change does not abort the batch: one malformed change from a buggy
 * provider must not stop a hundred good ones from landing, or a single bad
 * device could freeze every other device. The rejected ids are returned so the
 * caller can report them rather than swallow them.
 */
export async function applyChanges(input: ApplyInput): Promise<ApplyResult> {
  const { db, businessId, deviceId, changes, now } = input;

  const accepted: SyncChange[] = [];
  const rejected: string[] = [];
  for (const raw of changes) {
    const validation = validateChange(raw, businessId);
    if (validation.ok) {
      accepted.push(validation.change);
    } else {
      rejected.push(
        `${(raw as { changeId?: string })?.changeId ?? 'unknown'}: ${validation.reason}`,
      );
    }
  }

  const conflicts: SyncConflict[] = [];
  let applied = 0;
  let skipped = 0;

  await db.transaction(
    'rw',
    [
      db.table(TABLE_FOR.business),
      db.table(TABLE_FOR.party),
      db.table(TABLE_FOR.product),
      db.table(TABLE_FOR.category),
      db.table(TABLE_FOR.taxRate),
      db.table(TABLE_FOR.stockLocation),
      db.table(TABLE_FOR.document),
      db.table(TABLE_FOR.documentLine),
      db.table(TABLE_FOR.documentLink),
      db.table(TABLE_FOR.payment),
      db.table(TABLE_FOR.paymentAllocation),
      db.table(TABLE_FOR.expense),
      db.table(TABLE_FOR.stockTransaction),
      db.syncConflicts,
      // Read-only here, and read for exactly one reason: a change whose id is in
      // THIS device's outbox is a mutation this device made, so when it comes
      // back it can be recognised as its own rather than mistaken for somebody
      // else's competing edit. See the equal-version check in `applyOne`.
      db.syncOutbox,
    ],
    async (tx: Transaction) => {
      for (const change of accepted) {
        const outcome = await applyOne(
          tx,
          change,
          { businessId, deviceId, now, fromSnapshot: input.fromSnapshot },
          conflicts,
        );
        if (outcome === 'applied') applied += 1;
        else skipped += 1;
      }
    },
  );

  return { applied, skipped, conflicts, rejected };
}

interface ApplyContext {
  readonly businessId: string;
  readonly deviceId: string;
  readonly now: string;
  readonly fromSnapshot: boolean;
}

async function applyOne(
  tx: Transaction,
  change: SyncChange,
  ctx: ApplyContext,
  conflicts: SyncConflict[],
): Promise<'applied' | 'skipped'> {
  const table = tx.table(TABLE_FOR[change.entityType]);
  const existing = (await table.get(change.entityId)) as Record<string, unknown> | undefined;

  // The isolation check, repeated here even though validateChange did it.
  //
  // This is the last line of defence before a write, and it is the one that
  // matters: a payload naming another business must never reach a table,
  // whatever produced it. Cheap to check, and it is the difference between an
  // isolated system and a cross-tenant data leak.
  const existingBusiness = typeof existing?.businessId === 'string' ? existing.businessId : null;
  if (existingBusiness !== null && existingBusiness !== change.businessId) return 'skipped';

  const localVersion = typeof existing?.syncVersion === 'number' ? existing.syncVersion : 0;

  // ── EQUAL VERSIONS, DIFFERENT MUTATIONS ───────────────────────────────────
  //
  // Two devices edited this row from the same base, so both computed version 2
  // and NEITHER edit is newer. A version cannot order them, and treating the
  // second one as a repeat — which is what `change.version <= localVersion` did —
  // deleted it without a word.
  //
  // So the first question is not "which version is this?" but "is this the SAME
  // mutation this device already made?". TWO facts answer it, and they are checked
  // in this order because each can mislead on its own:
  //
  //   1. the local OUTBOX. A change id sitting in `syncOutbox` is a mutation that
  //      originated here, and the engine pushes before it pulls, so every change
  //      this device sends comes back to it in the very same run — still in the
  //      outbox when it arrives. This check has to be right or every sync raises a
  //      conflict against the device's own work.
  //
  //      Not the device id instead: a restored install inherits the device id of
  //      the machine it was restored from, and then "same device" is not "same
  //      mutation" — two machines would quietly discard each other's edits.
  //
  //   2. the change id recorded ON the row, for a mutation applied from a peer.
  //      The outbox cannot know about those, so without this the same change
  //      arriving a second time — a redelivery after a crash between the local
  //      commit and the cursor write — would be read as a collision and raise a
  //      conflict against work already done. A recorded origin can go stale (a
  //      local write spreads the row and leaves it naming the previous version),
  //      but it can then only DISAGREE with an incoming change, never wrongly
  //      agree with one, so reading it after the outbox is safe.
  //
  // Anything else is a COLLISION: the domain decides what that means for this
  // class of record, and the decision is persisted — never applied silently.
  if (!ctx.fromSnapshot && change.version === localVersion) {
    //   1. the local OUTBOX. A change id sitting in `syncOutbox` is a mutation that
    //      originated here, and the engine pushes before it pulls, so every change
    //      this device sends comes back to it in the very same run — still in the
    //      outbox when it arrives. This check has to be right or every sync raises a
    //      conflict against the device's own work.
    //
    //      Not the device id instead: a restored install inherits the device id of
    //      the machine it was restored from, and then "same device" is not "same
    //      mutation" — two machines would quietly discard each other's edits.
    //
    //      That concern is addressed by checking deviceId ONLY as a fallback when
    //      both the outbox lookup AND the row-level syncChangeId check have already
    //      failed. When both are silent, the outbox is provably missing — which only
    //      happens in the backup/restore gap (syncOutbox is excluded from
    //      BACKUP_TABLES). In that window, the server-asserted deviceId is the only
    //      remaining authoritative evidence of origin: the provider recorded it from
    //      this device's own push request, and the server's changeId primary-key
    //      constraint guarantees that a change carrying this id was filed AT MOST
    //      once — so a matching deviceId means this device filed it, and a
    //      different mutation cannot share the changeId.
    //
    //      The same reasoning also covers the stale-version path (below): a redelivered
    //      v1 while the row has moved to v2 still carries our deviceId, and the outbox
    //      entry — if present — would already have caught it; if absent, the deviceId
    //      is the authoritative fallback.
    //
    //   2. the change id recorded ON the row, for a mutation applied from a peer.
    //      The outbox cannot know about those, so without this the same change
    //      arriving a second time — a redelivery after a crash between the local
    //      commit and the cursor write — would be read as a collision and raise a
    //      conflict against work already done. A recorded origin can go stale (a
    //      local write spreads the row and leaves it naming the previous version),
    //      but it can then only DISAGREE with an incoming change, never wrongly
    //      agree with one, so reading it after the outbox is safe.
    //
    //   3. the device id asserted by the server, as a LAST RESORT.
    //      Only consulted when both evidence above is absent (the backup/restore
    //      gap), so it never overrides a real outbox or syncChangeId check.
    //
    // Anything else is a COLLISION: the domain decides what that means for this
    // class of record, and the decision is persisted — never applied silently.
    const outboxHit = (await tx.table('syncOutbox').get(change.changeId)) !== undefined;
    const changeIdOnRow = existing?.syncChangeId === change.changeId;
    const deviceIdMatch = change.deviceId === ctx.deviceId;

    if (isDiagnosticEnabled() && deviceIdMatch && !outboxHit && !changeIdOnRow) {
      console.warn('[DIAGNOSTIC] deviceId fallback caught self-echo (equal-version)', {
        entityType: change.entityType,
        entityId: change.entityId,
        changeId: change.changeId,
        remoteDeviceId: change.deviceId,
        localDeviceId: ctx.deviceId,
        localVersion,
        remoteVersion: change.version,
        syncChangeId: existing?.syncChangeId,
      });
    }

    const ours = outboxHit || changeIdOnRow || deviceIdMatch;

    if (!ours) {
      // A COLLISION MUST DISAGREE ABOUT SOMETHING.
      //
      // Two live edits at one version are a question for a person, but only when
      // the two states actually differ. A change whose payload is identical to
      // the row — every non-sync key equal, in both directions — cannot be one of
      // two competing versions: applying it would not change a single value, and
      // asking the user to choose between two identical records is a question
      // with no wrong answer.
      //
      // This is reachable, not hypothetical. A device that restored a business
      // from the cloud re-publishes that row under a NEW change id (the id on the
      // restored row names the CLOUD device's mutation, and re-sending that id is
      // refused — see ensureBusinessChangeEnqueued). The cloud's original change
      // then arrives again, at the same version, from a different device; without
      // this rule every such redelivery fabricated a conflict about a business
      // nobody had edited.
      if (sameContentAsRow(change.payload, existing)) return 'skipped';

      if (isDiagnosticEnabled()) {
        console.warn('[DIAGNOSTIC] CONFLICT (equal-version): self-echo checks all failed', {
          entityType: change.entityType,
          entityId: change.entityId,
          changeId: change.changeId,
          remoteDeviceId: change.deviceId,
          localDeviceId: ctx.deviceId,
          localVersion,
          remoteVersion: change.version,
          syncChangeId: existing?.syncChangeId,
          outboxHit,
          changeIdOnRow,
          deviceIdMatch,
          reason: 'all self-echo checks failed — genuine concurrent edit',
        });
      }
      const decision = resolveConflict({
        entityType: change.entityType,
        recordClass: classOfEntity(change.entityType),
        localVersion,
        remoteVersion: change.version,
        concurrent: true,
      });
      // Nothing is written either way. Without field-level information the model
      // answers REQUIRES_USER for every class that reaches here, so the row the
      // user has been looking at is kept and the competing change is logged for a
      // person to settle. If field information is ever plumbed through, the model
      // could answer MERGE — and this branch must then be taught to merge rather
      // than to keep recording a conflict.
      const conflict = await persistConflict(
        tx,
        change,
        ctx,
        decision.reason,
        localVersion,
        statusOfRecord(existing),
      );
      if (conflict) conflicts.push(conflict);
      return 'skipped';
    }
  }

  if (change.operation === 'DELETE') {
    if (!ctx.fromSnapshot && change.version <= localVersion) return 'skipped';

    // AN APPEND-ONLY LEDGER IS NOT DELETABLE, and the check has to live HERE.
    //
    // `conflictModel.ts` already owns this answer (`LEDGER_APPEND_ONLY`, "never
    // permitted"), but this branch returns before that model is ever asked — so
    // a deletion naming a stock movement used to be carried out in silence. The
    // movement disappeared, every balance after it moved, and nothing in the
    // product said why: no conflict row, no audit entry, no error. The correction
    // a shop is supposed to make is a cancellation or a return, which post a NEW
    // movement — and there is nothing left to correct once the original is gone.
    if (classOfEntity(change.entityType) === 'STOCK_LEDGER') {
      const conflict = await persistConflict(
        tx,
        change,
        ctx,
        'LEDGER_APPEND_ONLY',
        localVersion,
        statusOfRecord(existing),
      );
      if (conflict) conflicts.push(conflict);
      return 'skipped';
    }

    // Nothing here to remove, and nothing here worth preserving. Deliberately not
    // an error: a device that never held the row has nothing to disagree about,
    // and inventing an empty tombstone would be a row that means nothing.
    if (!existing) return 'skipped';

    // A TOMBSTONE ON THE ROW — for EVERY entity type, not only the finished ones.
    //
    // This used to physically delete anything that was not a document or a
    // payment, and the damage was silent, one-sided, and permanent. Three things
    // are true of deleting instead, and all three are why this is a tombstone:
    //
    //  1. THE VERSION DISAPPEARS WITH THE ROW. `localVersion` is read from the
    //     row, so a deleted row reads as version ZERO — and every change still
    //     carrying that row then applies, because there is no longer a version to
    //     compare against. A customer deleted at version 2 comes back the moment
    //     any older change naming them arrives, and it comes back on THIS device
    //     only: the device that originated the deletion holds version 2 and skips
    //     the same change forever. The two never converge again. The tombstone
    //     keeps the version, which is the whole defence.
    //  2. THE TWO DEVICES DISAGREE. `deleteParty` on the device that ORIGINATED
    //     the deletion keeps the row and clears `isActive`, so a receiving device
    //     that removed the row is holding something DIFFERENT from the device
    //     that sent the change. `partyReader` lists inactive parties on purpose —
    //     "a ledger that hides deactivated customers cannot be reconciled" — so
    //     the customer with invoices outstanding appears on one device and is
    //     missing from the other, and no amount of refreshing fixes it.
    //  3. THE FOLLOW-UP WORK BECOMES IMPOSSIBLE. The customer cannot be edited
    //     again, and a new document cannot be raised for them: `invoiceService`
    //     refuses an invoice whose customer is not in the table. That is the one
    //     case a shop actually needs — a credit note against the invoice that was
    //     already issued — and it would be blocked on the device that received
    //     the deletion while remaining possible on the device that made it.
    const row = existing as Record<string, unknown>;
    await table.put({
      ...row,
      // Only rows that HAVE the flag are switched off. An invoice is deleted by
      // its status, and inventing an `isActive` on it would be a second,
      // contradicting way to say the same thing.
      ...(typeof row.isActive === 'boolean' ? { isActive: false } : {}),
      // The timestamp is what makes the tombstone ageable. Without it compaction
      // can never drop it, so it would pin the change log forever.
      syncDeletedAt: change.deletedAt,
      syncVersion: change.version,
      // Which mutation produced this state, so a redelivery of this same tombstone
      // is recognised as one rather than as another device's competing edit.
      syncChangeId: change.changeId,
    });
    return 'applied';
  }

  if (change.version <= localVersion) {
    if (ctx.fromSnapshot) return 'skipped';

    // Self-echo at a STALE version: a change this device already published and
    // later superseded. The provider redelivers history — not just the latest
    // change per entity — so a document that moved v1→v2→v3 re-delivers all
    // three. The equal-version block above catches v3 (our current version);
    // v1 and v2 arrive here, at a lower version, with no self-echo guard and
    // therefore no way to tell "my own older mutation" from "a different
    // device's conflicting edit."
    //
    // The outbox lookup is the same signal as the equal-version block: if this
    // changeId was minted on THIS device, the provider is sending it back. The
    // row-level check (`syncChangeId`) does not help here — the row has moved
    // on and `syncChangeId` now names the newer mutation — but the outbox entry
    // persists (it is only pruned after every device has acknowledged past it,
    // and this device's cursor is still on the batch that contained it).
    //
    // Without this guard, a single issued invoice produces three conflicts
    // (one per historical version) instead of zero, and the count grows on
    // every sync because the provider re-sends the same history each time the
    // cursor advances.
    //   1. a row that exists for THIS changeId — the outbox entry
    //      persists (it is only pruned after every device has acknowledged past it,
    //      and this device's cursor is still on the batch that contained it).
    //   2. FAILSAFE: the device id this device used to file the change.
    //      The outbox lookup is the FIRST and preferred signal: it is a proof that
    //      this device minted the changeId and is therefore the most reliable.
    //      But in the backup/restore gap the outbox is lost (syncOutbox is not in
    //      BACKUP_TABLES), so the outbox has nothing to say. The device id, as
    //      recorded by the provider from THIS device's own push, is the
    //      authoritative remainder: if the server says we filed it, we filed it.
    const outboxHit = (await tx.table('syncOutbox').get(change.changeId)) !== undefined;
    const deviceIdMatch = change.deviceId === ctx.deviceId;

    if (isDiagnosticEnabled() && deviceIdMatch && !outboxHit) {
      console.warn('[DIAGNOSTIC] deviceId fallback caught self-echo (stale-version)', {
        entityType: change.entityType,
        entityId: change.entityId,
        changeId: change.changeId,
        remoteDeviceId: change.deviceId,
        localDeviceId: ctx.deviceId,
        localVersion,
        remoteVersion: change.version,
      });
    }

    const ours = outboxHit || deviceIdMatch;
    if (ours) {
      return 'skipped';
    }

    return 'skipped';
  }

  // A ROW THIS DEVICE HAS NEVER SEEN IS A FIRST ARRIVAL, not a divergence.
  //
  // There is no local state for the incoming mutation to diverge FROM, and
  // `resolveConflict` cannot know that: asked about a finalized document it does
  // not hold, the FINALIZED branch refuses — because it only knows whether the two
  // sides' CHANGED FIELDS overlap, and it is told nothing about a row that does
  // not exist. Absent field information therefore read as "these disagree", and an
  // invoice a customer had never seen was refused on every device except a
  // brand-new one (which arrives by snapshot, where conflicts are suppressed).
  //
  // So the fix belongs HERE, in the caller that knows whether a local row exists,
  // and not in the model: the model is correct to refuse a divergence, and it is
  // the absence of a divergence that the caller was failing to pass on.
  //
  // Note this is about the LOCAL ROW, not the entity type or the document's
  // status. Every entity is handled by the same rule — a payment, a movement and a
  // cancelled invoice all belong on a device that does not have them yet — and the
  // rules below still apply in full the moment a local row DOES exist.
  if (!ctx.fromSnapshot && existing !== undefined) {
    if (existing?.syncChangeId === change.changeId) {
      return 'skipped';
    }
    // ── SELF-ECHO AT A STRICTLY NEWER VERSION ─────────────────────────
    //
    // A row that this device created or last wrote LACKS a syncChangeId —
    // either created via bulkAdd without sync metadata (documentLine,
    // paymentAllocation, business) or a legacy row predating the sync
    // protocol. The server is echoing back a change THIS device pushed,
    // and it arrives at a higher version than the local row's (0).
    //
    // deviceId is NOT the sole signal. It is combined with the ABSENCE of
    // syncChangeId on the row: if the row DID carry a syncChangeId, a
    // mismatch would mean a DIFFERENT mutation — not a self-echo — and the
    // lifecycle/transition checks below would decide it. The absence of
    // syncChangeId is what proves there is no prior mutation identity for
    // a competing edit to conflict with.
    //
    // When all conditions are true, this is provably the device's own
    // mutation:
    //  1. No syncChangeId → no prior mutation identity on the row
    //  2. deviceId matches → the server recorded this device as the filer
    //  3. version > localVersion → the change is newer (not a retry)
    //
    // The change is APPLIED (not skipped) because the local row lacks
    // syncVersion/syncChangeId — backfilling them via table.put is the
    // point. The payload is THIS device's own data as echoed by the
    // server, so no unrelated local edits are overwritten.
    const knownSyncChangeId = existing?.syncChangeId;
    const isSelfEchoNoSyncId = !knownSyncChangeId && change.deviceId === ctx.deviceId;

    if (isDiagnosticEnabled()) {
      if (isSelfEchoNoSyncId) {
        console.warn(
          '[DIAGNOSTIC] deviceId fallback caught self-echo (newer-version: no syncChangeId)',
          {
            entityType: change.entityType,
            entityId: change.entityId,
            changeId: change.changeId,
            remoteDeviceId: change.deviceId,
            localDeviceId: ctx.deviceId,
            localVersion,
            remoteVersion: change.version,
            syncChangeId: knownSyncChangeId,
          },
        );
      } else {
        console.warn(
          '[DIAGNOSTIC] CONFLICT (newer-version): syncChangeId mismatch on existing row',
          {
            entityType: change.entityType,
            entityId: change.entityId,
            changeId: change.changeId,
            remoteDeviceId: change.deviceId,
            localDeviceId: ctx.deviceId,
            localVersion,
            remoteVersion: change.version,
            syncChangeId: knownSyncChangeId,
            syncVersion: existing?.syncVersion,
          },
        );
      }
    }

    if (!isSelfEchoNoSyncId) {
      // Only run lifecycle/transition/conflict checks when this is NOT a
      // confirmed self-echo. A confirmed self-echo falls through to the
      // table.put below to backfill sync metadata.
      // A legitimate lifecycle transition (DRAFT -> ISSUED -> CANCELLED) is the same
      // document progressing through its states, one mutation per step. Each step is
      // a NEW changeId and a NEW version, and the only fields that change are the
      // lifecycle ones — status, issuedAt, cancelledAt, etc. The financial data and
      // line items stay exactly what they were, because issuing and canceling do not
      // touch them.
      //
      // A divergent edit is the same version step but with different financial data:
      // someone changed grandTotalPaise on device A while device B still holds the
      // draft. That is NOT a transition and MUST be refused — an issued invoice is a
      // legal document, and silently taking the remote financials over a local edit
      // is exactly the silent rewrite this model exists to prevent.
      //
      // So the question is not "does the remote differ from local?" — it always does,
      // because the version number is different by definition. The question is "does
      // it differ ONLY in lifecycle fields, and to a status that legally follows?"
      // When both answers are yes, the change is a peer's own lifecycle transition
      // and is applied. When either is no, the existing conflict model handles it.
      if (isLegitLifecycleTransition(existing, change)) {
        // Not a divergence; not a retry (retry is caught by the equal-version block
        // above). Apply it as a straightforward newer change.
      } else {
        const decision = resolveConflict({
          entityType: change.entityType,
          recordClass: classOfEntity(change.entityType),
          localVersion,
          remoteVersion: change.version,
        });
        if (decision.resolution === 'REQUIRES_USER') {
          // NOT applied. Writing a conflict and moving on is the whole point: a
          // blocked device is a broken device, and an unapplied change is
          // recoverable where a silently-rewritten ledger is not.
          const conflict = await persistConflict(
            tx,
            change,
            ctx,
            decision.reason,
            localVersion,
            statusOfRecord(existing),
          );
          if (conflict) conflicts.push(conflict);
          return 'skipped';
        }
      }
    }
  }

  // `businesses` is the one table whose row does not carry its own businessId:
  // the row IS the business (see `Business` in db/types). Writing one adds a
  // property the type does not declare, and `businessPayloadMatches` compares
  // EVERY non-sync key of the row against the payload frozen in the outbox —
  // so the injected property makes the row disagree with its own change, and
  // the next recovery is refused with PAYLOAD_MISMATCH for a modification that
  // never happened. A payload that already carries the field keeps it; this
  // only stops the engine from adding one.
  await table.put({
    ...(change.payload as Record<string, unknown>),
    id: change.entityId,
    ...(change.entityType === 'business' ? {} : { businessId: change.businessId }),
    syncVersion: change.version,
    syncUpdatedAt: change.changedAt,
    // Which mutation produced this state. The outbox recognises a mutation this
    // device just made; this recognises one it applied from a peer, so that change
    // arriving twice is an idempotent no-op instead of a phantom conflict.
    syncChangeId: change.changeId,
  });
  return 'applied';
}

/**
 * The stable identity of a logical conflict.
 *
 * A conflict is about ONE incoming mutation colliding with THIS device's state.
 * That identity is fully determined by:
 *   - the business (tenant boundary)
 *   - the entity being contested (entityType+entityId)
 *   - the remote mutation that caused it (remoteDeviceId+changeId)
 *
 * Two re-deliveries of the SAME change carry the SAME identity, so they resolve
 * to the SAME conflict row — which means a crash between the local commit and the
 * cursor write, or a cursor that does not advance, does NOT multiply conflict
 * rows. A DIFFERENT change from the same device, or from a different device,
 * has a DIFFERENT changeId and therefore a DIFFERENT identity, so it is allowed
 * to create a distinct conflict.
 *
 * The `conflict:` prefix distinguishes these from ULID-based ids generated by
 * `newId()`, so a deterministic id can never collide with a random one.
 */
function conflictIdFor(
  businessId: string,
  entityType: string,
  entityId: string,
  remoteDeviceId: string,
  changeId: string,
): string {
  return `conflict:${businessId}:${entityType}:${entityId}:${remoteDeviceId}:${changeId}`;
}

/**
 * Persist a conflict idempotently.
 *
 * A conflict is identified by businessId:entityType:entityId:remoteDeviceId:changeId,
 * so the SAME incoming change re-processed after a crash or cursor rollback
 * resolves to the SAME row instead of creating a duplicate.
 *
 * Returns the NEW conflict on creation, or NULL when the conflict already
 * exists (resolved or not). NULL tells the caller "this is not a new conflict
 * for this run": the `conflicts` array tracks what to REPORT, not what already
 * existed, so a re-delivered change does not inflate the count.
 *
 * This function is called inside `applyChanges`'s transaction, so the lookup
 * and the write are atomic.
 */
async function persistConflict(
  tx: Transaction,
  change: SyncChange,
  ctx: ApplyContext,
  reason: SyncConflict['reason'],
  localVersion: number,
  localStatus: string | undefined,
): Promise<SyncConflict | null> {
  const id = conflictIdFor(
    change.businessId,
    change.entityType,
    change.entityId,
    change.deviceId,
    change.changeId,
  );

  const existing = await tx.table('syncConflicts').get(id);
  if (existing) {
    // Preserve the original detection time, resolution state, and details.
    // A retry must not erase that a person already resolved this conflict,
    // nor fabricate a new detection time that hides how long the issue
    // has been open. Return null so the caller does not count it as a NEW
    // conflict for this run.
    return null;
  }

  const conflict: SyncConflict = {
    conflictId: id,
    businessId: change.businessId,
    entityType: change.entityType,
    entityId: change.entityId,
    localDeviceId: ctx.deviceId,
    remoteDeviceId: change.deviceId,
    reason,
    localVersion,
    remoteVersion: change.version,
    detectedAt: ctx.now,
    // The sentence is built from BOTH sides' own facts, not from the reason
    // alone: "another device" is false when both device ids are this one, and
    // "issued here" is false when this device's copy is still a draft — or the
    // incoming one is.
    guidance: explainConflict({
      reason,
      entityType: change.entityType,
      localDeviceId: ctx.deviceId,
      remoteDeviceId: change.deviceId,
      localStatus,
      remoteStatus: documentStatusOf(change),
    }),
    resolved: false,
    // THE CHANGE ITSELF, so the refusal survives the cursor advancing past it.
    // Without this the row is a warning about a payload no device can reach.
    ...pendingChangeForStorage(change),
  };

  await tx.table('syncConflicts').put(conflict);
  return conflict;
}

/**
}

/**
 * The fields that a legitimate document lifecycle transition (issue, cancel,
 * payment, return) is ALLOWED to change.
 *
 * Any field NOT in this set — every financial amount, every line, every charge,
 * the customer id — must be IDENTICAL between local and remote for the incoming
 * change to bypass the conflict check. If a non-lifecycle field differs, the
 * change is either a divergent edit or data corruption, and must be refused.
 *
 * This is deliberately a CONSERVATIVE allow-list, not a deny-list. A deny-list
 * would have to be updated whenever a new financial field is added; an allow-list
 * defaults to "refuse" for any field we did not anticipate, which is the safe
 * direction for accounting data.
 */
const LIFECYCLE_ALLOWED_FIELDS: ReadonlySet<string> = new Set([
  // Status transitions and their lifecycle timestamps.
  'status',
  'issuedAt',
  'cancelledAt',
  'cancelledBy',
  'cancelReason',
  'returnedAt',
  // Audit trail: who and when.
  'updatedAt',
  'updatedBy',
  // Version ordering.
  'syncVersion',
  // Derived rollups: recomputed from payment_allocations, never user-edited.
  // A payment or return legitimately changes these alongside the status.
  'allocatedPaise',
  'balancePaise',
  // Computed from totals: changes only when the underlying totals change,
  // which is not a lifecycle event and would be caught by the fields above.
  'calcVersion',
  'calcSnapshot',
]);

/**
 * True when the incoming change is a valid lifecycle progression of the existing
 * local document — not a divergent edit.
 *
 * Two conditions must both hold:
 *
 *  1. The incoming status is a valid successor of the local status, per the
 *     document lifecycle rules. DRAFT -> ISSUED and ISSUED -> CANCELLED qualify;
 *     ISSUED -> DRAFT and DRAFT -> SETTLED do not.
 *
 *  2. Every field the lifecycle is NOT allowed to touch is identical between the
 *     local row and the incoming payload. This is what distinguishes "the same
 *     document, next lifecycle step" from "someone changed the total while nobody
 *     was looking".
 *
 * If either fails, the change falls through to `resolveConflict`, which preserves
 * the `FINALIZED_DIVERGENCE` refusal for genuine edits. A valid transition that
 * happens to also change a financial field is thus refused — correctly, because a
 * lifecycle event that also rewrites the grand total is not a clean transition.
 */
function isLegitLifecycleTransition(
  existing: Record<string, unknown>,
  change: SyncChange,
): boolean {
  if (change.entityType !== 'document') return false;
  if (!change.payload || typeof change.payload !== 'object') return false;

  const localStatus = existing.status as DocStatus | undefined;
  const remoteStatus = change.payload.status as DocStatus | undefined;
  if (!localStatus || !remoteStatus) return false;
  if (localStatus === remoteStatus) return false;

  const next = allowedTransitions(localStatus);
  if (!next.includes(remoteStatus)) return false;

  // Every non-lifecycle field on the incoming payload must match the local row.
  // Lifecycle fields are explicitly allowed to differ; everything else must be
  // bit-for-bit identical, or this is a divergent edit, not a transition.
  const remote = change.payload as Record<string, unknown>;
  for (const key of Object.keys(remote)) {
    if (LIFECYCLE_ALLOWED_FIELDS.has(key)) continue;
    if (deepEqual(existing[key], remote[key])) continue;
    return false;
  }
  // Reverse check: every non-lifecycle, non-sync-metadata field on the LOCAL row
  // must also appear in the remote payload. A lifecycle transition carries the
  // COMPLETE document — only status fields may change. A field that exists locally
  // but is absent from the remote cannot be verified, and the UPSERT path (table.put)
  // would silently drop it. This closes the omission gap: the forward loop only
  // inspects keys present in the remote, so a missing financial field would
  // otherwise pass unchecked.
  //
  // Sync metadata fields are excluded because the apply path writes them
  // independently of the payload (syncChangeId at line 400, syncDeletedAt only
  // during tombstone handling at line 307).
  for (const key of Object.keys(existing)) {
    if (LIFECYCLE_ALLOWED_FIELDS.has(key)) continue;
    if (key.startsWith('sync')) continue;
    if (!(key in remote)) return false;
  }
  return true;
}

/**
 * A structural equality check for payload fields.
 *
 * The wire shape is JSON, so the payloads are plain values — no class instances,
 * no functions, no undefined. Arrays and objects compare by content; null and
 * object are never confused.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, i) => deepEqual(val, b[i]));
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;
  const ka = Object.keys(a as Record<string, unknown>);
  const kb = Object.keys(b as Record<string, unknown>);
  if (ka.length !== kb.length) return false;
  return ka.every((k) =>
    deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

/**
 * Is the incoming payload the SAME RECORD STATE the local row already holds?
 *
 * `sync*` keys are excluded: they describe which mutation produced the state,
 * not the state itself, and they are exactly what differs between a change this
 * device applied and one it re-published. Everything else must match, in both
 * directions — an extra key on one side is a difference.
 */
function sameContentAsRow(
  payload: Record<string, unknown> | undefined,
  existing: Record<string, unknown> | undefined,
): boolean {
  if (!payload || !existing) return false;
  const keys = new Set([
    ...Object.keys(payload).filter((key) => !key.startsWith('sync')),
    ...Object.keys(existing).filter((key) => !key.startsWith('sync')),
  ]);
  for (const key of keys) {
    if (!deepEqual(payload[key], existing[key])) return false;
  }
  return true;
}
